import { LOCATION_MAP } from '@/constants/jobOptions'

/**
 * Utility functions for formatting various data types consistently across the app
 */

/**
 * Formats a role slug into a human-readable string
 * @param role - The role slug (e.g., "dp-cinematographer")
 * @returns Formatted role name (e.g., "DP/Cinematographer")
 */
export const formatRole = (role: string): string => {
  return role
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Formats a project type slug into a human-readable string
 * @param type - The project type slug (e.g., "feature-film")
 * @returns Formatted project type (e.g., "Feature film")
 */
export const formatProjectType = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

/**
 * Formats a location slug into a full location name
 * @param location - The location slug (e.g., "new-york")
 * @returns Formatted location (e.g., "New York, NY") or original if not mapped
 */
export const formatLocation = (location: string): string => {
  return LOCATION_MAP[location] || location
}

/**
 * Formats a date string into a relative time description
 * @param dateString - ISO date string
 * @returns Human-readable relative time (e.g., "2 days ago", "Today")
 */
export const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return 'Today'
  if (diffInDays === 1) return 'Yesterday'
  if (diffInDays < 7) return `${diffInDays} days ago`
  return date.toLocaleDateString()
}

/**
 * Formats a project date string for display
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string
 */
export const formatProjectDate = (dateString: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString + 'T00:00:00')
  return date.toLocaleDateString()
}

/**
 * Formats a pay type slug into a human-readable string
 * @param payType - The pay type slug (e.g., "paid-flat-rate")
 * @returns Formatted pay type (e.g., "Paid (Flat Rate)")
 */
export const formatPayType = (payType: string): string => {
  if (!payType) return 'Pay TBD'
  
  const payTypeMap: Record<string, string> = {
    'paid-flat-rate': 'Paid (Flat Rate)',
    'paid-hourly': 'Paid (Hourly)',
    'paid-day-rate': 'Paid (Day Rate)',
    'deferred-payment': 'Deferred Payment',
    'unpaid-credit-only': 'Unpaid (Credit Only)',
    'unpaid-volunteer': 'Unpaid (Volunteer)',
    'rate-negotiable': 'Rate Negotiable',
    'union-rate': 'Union Rate (SAG/AFTRA, etc.)',
    'tbd': 'TBD (To Be Determined)'
  }
  
  return payTypeMap[payType] || payType.charAt(0).toUpperCase() + payType.slice(1)
}

/**
 * Gets the appropriate badge variant for a job status
 * @param status - Job status string
 * @returns Badge variant name
 */
export const getJobStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'outline' => {
  switch (status) {
    case 'active':
      return 'default'
    case 'pending':
      return 'secondary'
    case 'closed':
      return 'outline'
    default:
      return 'default'
  }
}
